import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Modal, ModalBody, ModalFooter, Button, Input } from "reactstrap";
import i18n from "../../../i18n";
import { putUpdateServices, getServiceAdmin, getProfile } from "../../../helpers/api_rombo_helper";

const StartServiceModal = ({ isOpen, toggle, selectedService, setShowAlertSuccess, setTextHeader, setTextBody }) => {
  const [note, setNote] = useState("");
  const [isNoteValid, setIsNoteValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [userProfile, setUserProfile] = useState({ username: '', sub: '' });

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({
      token: Login.token,
      loading: Login.loading,
      data: Login.data,
    })
  );

  const { token } = useSelector(LoginProperties);

  useEffect(() => {
    if (token) {
      getProfile(token)
        .then((response) => {
          setUserProfile({
            username: response.full_name || '',
            sub: response.id || ''
          });
        })
        .catch((error) => {
          console.error("Error getting user profile:", error);
        });
    }
  }, [token]);

  const handleStartService = async () => {
    if (!note.trim()) return;

    setIsLoading(true);

    try {
      const serviceCode = selectedService.code;
      const serviceData = await getServiceAdmin(token, serviceCode);
      const driverSub = serviceData?.driver?.sub;

      if (!driverSub) {
        throw new Error("Driver sub not found in service data");
      }

      const payload = {
        id: serviceCode,
        driver_sub: driverSub,
        status: "ACTIVE",
        started_by: userProfile.username || userProfile.sub,
      };

      await putUpdateServices(token, payload);

      setNote("");
      toggle();
      setShowAlertSuccess(true);
      setTextHeader(i18n.t("ServiceStartedRemotely"));
      setTextBody(i18n.t("ServiceStartedRemotelyBody"));

    } catch (error) {
      console.error("Error starting service:", error);
      setHasError(true);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 1000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      role="dialog"
      autoFocus={false}
      centered
      className={`RequestModal ${isShaking ? "modal-error" : ""}`}
      toggle={toggle}
      backdrop="static"
      size="lg"
    >
      <div>
        <div className="modal-header-container">
          <p className="cancellation-title modal-header-bottom">
            {i18n.t("StartServiceRemotely")}
          </p>
          <p className="cancellation-text modal-service-id">
            {selectedService?.code}
          </p>
        </div>
        <div className="modal-separator" />
        <ModalBody>
          <p className="modal-text-bold">
            {i18n.t("StartServiceText")}
          </p>
          {hasError && (
            <div className="alert alert-danger mb-3" role="alert">
              {i18n.t("StartServiceError")}
            </div>
          )}
          <Input
            type="textarea"
            className={`form-control ${hasError ? "is-invalid" : ""}`}
            placeholder={i18n.t("WriteYourNoteHere")}
            value={note}
            maxLength={200}
            onChange={(e) => {
              const value = e.target.value.slice(0, 200);
              setNote(value);
              setIsNoteValid(value.trim() !== "");

              if (hasError) setHasError(false);
            }}
          />
          <small className="text-muted">
            {note.length}/200 {i18n.t("Characters")}
          </small>
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn btn-users border"
            type="button"
            onClick={handleStartService}
            disabled={!isNoteValid || isLoading}
          >
            {isLoading ? i18n.t("Starting...") : i18n.t("Accept")}
          </Button>
          <Button className="btn btn-light" type="button" onClick={toggle}>
            {i18n.t("Cancel")}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

StartServiceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  selectedService: PropTypes.object.isRequired,
  setShowAlertSuccess: PropTypes.func.isRequired,
  setTextHeader: PropTypes.func.isRequired,
  setTextBody: PropTypes.func.isRequired,
};

export default StartServiceModal;
